import { collectionCardTemplate, productCardTemplate, ROOT_ACCEPTS, type ElementNodeDto, type ElementType } from '@xshop/shared';
import type { ElementCategory, ElementDefinition } from './catalog/definition';
import { columnElement, containerElement, popupElement, rowElement, sectionElement } from './catalog/layouts';
import { headingElement, listElement, paragraphElement } from './catalog/texts';
import { audioElement, iconElement, imageElement, videoElement } from './catalog/media';
import { blockLinkElement, buttonElement, linkElement } from './catalog/actions';
import {
  checkboxElement, fileUploadElement, formElement, inputElement, labelElement,
  selectElement, starsElement, textareaElement
} from './catalog/forms';
import { customHtmlElement, dividerElement, spacerElement } from './catalog/other';
import { menuElement } from './catalog/menu';
import { cartElement, searchElement } from './catalog/commerce';
import { slideshowElement } from './catalog/slideshow';
import { tabsElement } from './catalog/tabs';
import { accordionElement } from './catalog/accordion';
import { marqueeElement } from './catalog/marquee';
import { bundleElement, offerElement, productScopeElement, purchaseElement, checkoutElement, orderSummaryElement, breadcrumbsElement, productMediaElement, socialLinksElement, swatchesElement } from './catalog/storefront';
import { collectionListElement, productListElement, reviewListElement } from './catalog/catalogLists';
import { listingFiltersElement, listingPaginationElement, listingToolbarElement } from './catalog/catalogListing';
import { stickyBuyElement } from './catalog/stickyBuy';
import { cartContentsElement } from './catalog/cartPage';
import { orderLookupElement } from './catalog/orderLookup';

// The one place an element type is bound to what it is: its name, its controls, the look it lands in and
// what may be dropped inside it. The builder's library, the inspector and the drop layer all answer
// their questions from here, so no two of them can hold a different opinion about what an element is.
//
// Adding an element is a definition in ./catalog plus its two lines below, an export line and a row.
// A type in `ELEMENT_TYPES` with no row here fails `tsc` on the `satisfies` at the bottom, rather than
// rendering as a blank box the first time a merchant drags it.

export * from './catalog/definition';
export * from './catalog/styleTokens';
export * from './catalog/layouts';
export * from './catalog/texts';
export * from './catalog/media';
export * from './catalog/actions';
export * from './catalog/forms';
export * from './catalog/other';
export * from './catalog/menu';
export * from './catalog/commerce';
export * from './catalog/slideshow';
export * from './catalog/marquee';
export * from './catalog/catalogLists';

/** Every element type mapped to its definition, in the order the shared vocabulary declares them. */
export const elementCatalog = {
  'section': sectionElement,
  'row': rowElement,
  'column': columnElement,
  'container': containerElement,
  popup: popupElement,
  'slideshow': slideshowElement,
  'tabs': tabsElement,
  'accordion': accordionElement,
  'marquee': marqueeElement,
  'heading': headingElement,
  'paragraph': paragraphElement,
  'list': listElement,
  'image': imageElement,
  'icon': iconElement,
  'video': videoElement,
  'audio': audioElement,
  'button': buttonElement,
  'link': linkElement,
  'block-link': blockLinkElement,
  'input': inputElement,
  'textarea': textareaElement,
  'select': selectElement,
  'checkbox': checkboxElement,
  'label': labelElement,
  'stars': starsElement,
  'file-upload': fileUploadElement,
  'form': formElement,
  'spacer': spacerElement,
  'divider': dividerElement,
  'custom-html': customHtmlElement,
  'menu': menuElement,
  'cart': cartElement,
  'search': searchElement,
  'collection-list': collectionListElement,
  'product-list': productListElement,
  'bundle': bundleElement,
  'product-scope': productScopeElement, purchase: purchaseElement, checkout: checkoutElement, 'order-summary': orderSummaryElement, offer: offerElement,
  breadcrumbs: breadcrumbsElement, 'product-media': productMediaElement, 'social-links': socialLinksElement, 'review-list': reviewListElement, swatches: swatchesElement,
  'listing-filters': listingFiltersElement, 'listing-toolbar': listingToolbarElement, 'listing-pagination': listingPaginationElement,
  'sticky-buy': stickyBuyElement, 'cart-contents': cartContentsElement, 'order-lookup': orderLookupElement
} satisfies Record<ElementType, ElementDefinition>;

/** The five groups, in the order the Add panel lists them. */
export const ELEMENT_CATEGORIES: readonly ElementCategory[] = [
  'Texts', 'Media', 'Forms', 'Layouts', 'Other'
];

/** Every element type a merchant can add, in the order the shared vocabulary declares them. */
export const elementTypes: readonly ElementType[] = Object.keys(elementCatalog) as readonly ElementType[];

// Reads one element's definition, which is how every other module asks what a type is.
export const elementDefinition = (type: ElementType): ElementDefinition => elementCatalog[type];

// Lists the types filed under one group, which is one column of the Add panel.
export const elementsInCategory = (category: ElementCategory): readonly ElementType[] =>
  elementTypes.filter((type) => elementCatalog[type].category === category);

// Answers the one question the drag layer asks of every hover: may this child be dropped in this parent?
export const acceptsElement = (parent: ElementType, child: ElementType): boolean =>
  elementCatalog[parent].accepts.includes(child);

// Reports whether an element takes no children at all, which is what stops a leaf drawing a drop zone.
export const isVoidElement = (type: ElementType): boolean => elementCatalog[type].void === true;

/**
 * Builds a fresh node of one type, carrying the catalog's own defaults. Nothing else may invent a node:
 * a hand-built one would start unstyled where every other copy of that element starts deliberate, and
 * the merchant would meet an element that looks broken the moment it lands.
 */
export const newElement = (type: ElementType, id: string): ElementNodeDto => {
  const { props, styles, bindings } = elementCatalog[type].defaults;
  return {
    id,
    type,
    props: { ...props },
    styles,
    children: type === 'collection-list' ? collectionCardTemplate(id) : type === 'product-list' || type === 'product-scope' ? productCardTemplate(id) : [],
    // A type that reads the shop lands reading it, the way Shopify's header lands on Main menu.
    ...(bindings ? { bindings: { ...bindings } } : {}),
  };
};

/**
 * Wraps a page's mint so every id handed out inside ONE build stays unique. A page-scanning mint hands
 * two siblings the same name when neither is on the page yet, and a subtree naming a node twice is
 * exactly what a saved tree is refused for — so a build remembers what it has already been given.
 */
export function buildMint(mint: (type: ElementType) => string): (type: ElementType) => string {
  const taken = new Set<string>();
  return (type) => {
    const base = mint(type);
    let id = base;
    for (let n = 2; taken.has(id); n += 1) id = `${base}-${n}`;
    taken.add(id);
    return id;
  };
}

// Builds one node and the starters under it, drawing every id from one already-deduplicating well.
function buildWithStarters(type: ElementType, mint: (type: ElementType) => string): ElementNodeDto {
  const node = newElement(type, mint(type));
  node.children = [...node.children, ...(elementCatalog[type].defaults.children ?? []).map((starter) => {
    const child = buildWithStarters(starter.type, mint);
    child.props = { ...child.props, ...starter.props };
    return child;
  })];
  return node;
}

/**
 * Builds a fresh node holding the starter children its definition declares, minting every id from the
 * caller's well. The elements whose content is their children — a menu, tabs, a carousel — land holding
 * something to see instead of an invisible empty box; a definition naming no starters lands exactly as
 * `newElement` builds it, so nothing about an existing page changes.
 */
export function newElementWithStarters(type: ElementType, mint: (type: ElementType) => string): ElementNodeDto {
  return buildWithStarters(type, buildMint(mint));
}

/**
 * Answers whether one type may hold another, including at the page root — which is not an element and
 * so has no definition of its own. This is the single lookup the tree, the drag layer and the keyboard
 * reorder all ask, so no two of them can hold different opinions about a legal page.
 */
export const elementAccepts = (parent: ElementType | null, child: ElementType): boolean =>
  parent === null ? ROOT_ACCEPTS.includes(child) : acceptsElement(parent, child);
